import { useEffect } from "react";
const SETTLE_MS = 180;
const FOCUS_FALLBACK_MS = 350;
const SCROLL_SUPPRESS_MS = 250;
const REVEAL_PADDING = 12;
const REVEAL_HYSTERESIS = 4;
const isIOSWebKit = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1;
const isEditable = (element: EventTarget | Element | null): element is HTMLElement => element instanceof HTMLElement && (element.isContentEditable || element.tagName === "INPUT" || element.tagName === "TEXTAREA");
const getCaretRect = (editable: HTMLElement): DOMRect => {
  const selection = window.getSelection?.();
  if (selection && selection.rangeCount > 0 && editable.contains(selection.anchorNode)) {
    const range = selection.getRangeAt(0);
    if (typeof range.getClientRects !== "function") return editable.getBoundingClientRect();
    const rects = range.getClientRects();
    const rect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
    if (rect.top !== 0 || rect.bottom !== 0 || rect.left !== 0) {
      return rect;
    }
  }
  return editable.getBoundingClientRect();
};
const getScrollableAncestors = (element: HTMLElement): HTMLElement[] => {
  const ancestors: HTMLElement[] = [];
  let node = element.parentElement;
  while (node && node !== document.body) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
      ancestors.push(node);
    }
    node = node.parentElement;
  }
  return ancestors;
};
const MAX_CONTEXT_RATIO = 0.8;
export const revealCaretInScrollableAncestors = (viewport: VisualViewport, editable: HTMLElement) => {
  const ancestors = getScrollableAncestors(editable);
  if (ancestors.length === 0) return;
  const context = editable.closest<HTMLElement>("[data-keyboard-reveal-context]") ?? editable;
  const capacity = Math.min(...ancestors.map(ancestor => {
    const bounds = ancestor.getBoundingClientRect();
    return Math.min(bounds.bottom, viewport.height) - Math.max(bounds.top, 0) - 2 * REVEAL_PADDING;
  }));
  const initialCaret = getCaretRect(editable);
  const initialContext = context.getBoundingClientRect();
  const fullHeight = initialContext.bottom - initialContext.top;
  const trailingHeight = initialContext.bottom - Math.min(initialCaret.top, initialContext.bottom);
  const mode = fullHeight <= capacity * MAX_CONTEXT_RATIO ? "full-context" : trailingHeight <= capacity * MAX_CONTEXT_RATIO ? "caret-to-context-end" : "caret";
  for (const ancestor of ancestors) {
    const caret = getCaretRect(editable);
    const contextRect = context.getBoundingClientRect();
    const target = mode === "full-context" ? {
      top: contextRect.top,
      bottom: contextRect.bottom
    } : mode === "caret-to-context-end" ? {
      top: Math.min(caret.top, contextRect.bottom),
      bottom: Math.max(caret.bottom, contextRect.bottom)
    } : {
      top: caret.top,
      bottom: caret.bottom
    };
    const bounds = ancestor.getBoundingClientRect();
    const visibleTop = Math.max(bounds.top, 0) + REVEAL_PADDING;
    const visibleBottom = Math.min(bounds.bottom, viewport.height) - REVEAL_PADDING;
    if (target.bottom > visibleBottom + REVEAL_HYSTERESIS) {
      ancestor.scrollTop += target.bottom - visibleBottom;
    } else if (target.top < visibleTop - REVEAL_HYSTERESIS) {
      ancestor.scrollTop -= visibleTop - target.top;
    }
  }
};
export const useIOSKeyboardScrollFix = () => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    const ios = isIOSWebKit();
    let disposed = false;
    let focusedEditable: HTMLElement | null = null;
    let keyboardClosing = false;
    let settleTimer = 0;
    let scrollSuppressedUntil = 0;
    let lastAppliedHeight = 0;
    let rafId = 0;
    let growthObserver: ResizeObserver | null = null;
    let observedContext: HTMLElement | null = null;
    let observedBlockSize = 0;
    const applyHeight = () => {
      if (viewport.scale !== 1) return;
      const height = Math.round(viewport.height);
      if (height === lastAppliedHeight) return;
      lastAppliedHeight = height;
      root.style.setProperty("--app-height", `${height}px`);
    };
    const settle = () => {
      if (disposed) return;
      settleTimer = 0;
      const editable = focusedEditable;
      keyboardClosing = false;
      applyHeight();
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        scrollSuppressedUntil = Date.now() + SCROLL_SUPPRESS_MS;
        window.scrollTo(0, 0);
      }
      if (editable && document.contains(editable)) {
        revealCaretInScrollableAncestors(viewport, editable);
      }
    };
    const scheduleSettle = (delay: number) => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, delay);
    };
    const onViewportChange = (event: Event) => {
      if (!ios || focusedEditable === null && !keyboardClosing) {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(applyHeight);
        return;
      }
      if (event.type === "scroll" && Date.now() < scrollSuppressedUntil) return;
      scheduleSettle(SETTLE_MS);
    };
    const onGrowth: ResizeObserverCallback = entries => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const nextBlockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      const previousBlockSize = observedBlockSize;
      observedBlockSize = nextBlockSize;
      if (nextBlockSize - previousBlockSize <= 0.5) return;
      const editable_0 = focusedEditable;
      if (disposed || !editable_0 || !observedContext?.isConnected) return;
      if (document.activeElement !== editable_0 && !editable_0.contains(document.activeElement)) return;
      revealCaretInScrollableAncestors(viewport, editable_0);
    };
    const observeGrowth = (editable_1: HTMLElement) => {
      growthObserver?.disconnect();
      if (typeof ResizeObserver === "undefined") return;
      observedContext = editable_1.closest<HTMLElement>("[data-keyboard-reveal-context]") ?? editable_1;
      observedBlockSize = observedContext.getBoundingClientRect().height;
      growthObserver ??= new ResizeObserver(onGrowth);
      growthObserver.observe(observedContext);
    };
    const stopObservingGrowth = () => {
      growthObserver?.disconnect();
      observedContext = null;
    };
    const onFocusIn = (event_0: FocusEvent) => {
      if (!isEditable(event_0.target)) return;
      focusedEditable = event_0.target;
      keyboardClosing = false;
      observeGrowth(event_0.target);
      scheduleSettle(FOCUS_FALLBACK_MS);
    };
    const onFocusOut = () => {
      window.setTimeout(() => {
        if (disposed || focusedEditable === null || isEditable(document.activeElement)) return;
        focusedEditable = null;
        keyboardClosing = true;
        stopObservingGrowth();
        scheduleSettle(FOCUS_FALLBACK_MS);
      }, 0);
    };
    applyHeight();
    viewport.addEventListener("resize", onViewportChange);
    viewport.addEventListener("scroll", onViewportChange);
    if (ios) {
      document.addEventListener("focusin", onFocusIn);
      document.addEventListener("focusout", onFocusOut);
    }
    return () => {
      disposed = true;
      viewport.removeEventListener("resize", onViewportChange);
      viewport.removeEventListener("scroll", onViewportChange);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      stopObservingGrowth();
      cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimer);
      root.style.removeProperty("--app-height");
    };
  }, []);
};